<script setup lang="ts">

import { onMounted, ref } from 'vue';
import { useVehicleApi } from '@app/MyGarage/Types/Api/VehicleApi';
import { formatDate } from '@library/Utilities';
import { ScanEvent } from '@domain/CarePlan/HealthMonitor/ScanEvent';

const { vehicleId: vehicleId } = defineProps<{ vehicleId: string }>();
let scanEvents = ref<ScanEvent[]>( [] );
let loading = ref( true );

onMounted( () =>
{
    fetchScanHistory();
} );

/**
 * Fetches error details for the current scan event and updates the errorCodes ref.
 * Sets the loading state to false once the fetch is complete which allows the page to render.
 */
const fetchScanHistory = async () =>
{

    try
    {
        const api = useVehicleApi();
        const result = await api.getScanHistory( vehicleId, 10 );
        scanEvents.value = result.scanEvents.map( e => new ScanEvent( e ) );
    }

    catch( error )
    {
        console.error( 'Failed to fetch error details:', error );
    }

    finally
    {
        loading.value = false;
    }
};

</script>

<template>
    <div class="h-full"
         :class="{
            'bg-gradient-to-b dark:from-gray-900 dark:to-gray-900': !loading && scanEvents.length == 0,
            'dark:bg-gray-850': !loading && scanEvents.length > 0
         }">

        <div class="pt-8 3xl:pt-10 px-4 lg:px-6 pb-2 3xl:pb-4 text-base lg:text-lg 3xl:text-xl
                    bg-gradient-to-b dark:from-gray-850 dark:to-gray-900">
            Scan History
        </div>

        <!-- THERE ARE NO SCAN EVENTS -->
        <div v-if="!loading && scanEvents.length == 0" class="p-4 lg:p-6">
            <h1 class="text-lg font-bold">No scan data available</h1>
            <p class="opacity-60">
                Carbucks has not yet recevied any telemetry scan events from your Health Monitor.  Please verify
                that it has been installed correctly and that you have completed at least one trip before
                checking again.
            </p>
        </div>

        <!-- LIST OF SCAN EVENTS -->
        <div v-else>
            <div v-for="event in scanEvents"
                class="p-4 lg:px-6 dark:border-y dark:border-b-gray-900 dark:border-t-gray-800 last:border-b-0
                       odd:bg-gray-50/20 dark:odd:bg-transparent text-base lg:text-lg 3xl:text-xl
                       flex items-center">
                <div class="mr-3 lg:mr-5">
                    <Icon v-if="event.hasIssue" name="alert-triangle" class="text-2xl text-rose-600" />
                    <Icon v-else name="check-circle" class="text-2xl text-emerald-500" />
                </div>
                <div class="flex-1">
                    <div>
                        <span v-if="event.hasIssue">Issue Detected</span>
                        <span v-else>No Issues Detected</span>
                    </div>
                    <div class="opacity-60 text-sm">
                        {{ formatDate( event.utcDateScanned, "ddd D MMM - HH:mm" ) }}
                    </div>
                </div>
                <RouterLink v-if="event.hasIssue"
                            :to="`/my-garage/scan-event/${event.scanEventId}/error-details`"
                            class="-mr-2 flex items-center">
                    <span class="text-xs uppercase dark:text-gray-500">Details</span>
                    <Icon name="chevron-right" class="text-2xl" />
                </RouterLink>
            </div>
        </div>

    </div>

</template>
